import {
  CloudArrowDown,
  Integrations,
  Plus,
} from '@sweep-io/sweep-design/dist/icons';
import { ActionsMenu, MenuPlacement } from '../../../../common/actions-menu/ActionsMenu';
import { importFunnelPanelType } from '../../../funnel-map-flow/dialogs/import-funnel/importFunnelPanelType';
import { useHandleCommentsAndAiFirst } from '../left-menu/useHandleCommentsAndAiFirst';
import { usePanels } from '../../../../panels/usePanels';
import { useDispatch } from 'react-redux';
import { useHasAllPermissions } from '../../../../common/permissions/useHasAllPermissions';
import { setAddOrImportDialogOpen } from '../../../../third-party-funnels/thirdPartyFunnelsReducer';
import { PopoverOrigin } from '@mui/material';

export enum AddFunnelMenuActions {
  IMPORT_FUNNEL = 'import-funnel',
  ADD_3TH_PARTY_FUNNEL = 'add-3th-party-funnel',
}

const DEFAULT_ALLOWED_MENU_ACTIONS = [
  AddFunnelMenuActions.IMPORT_FUNNEL,
  AddFunnelMenuActions.ADD_3TH_PARTY_FUNNEL,
];
const TOOLBAR_ACTIONS_SX = {
  '& .MuiListItemIcon-root': {
    minWidth: 0,
    paddingRight: 1,
  },
  '& .MuiButtonBase-root': {
    paddingLeft: '8px',
    paddingRight: '8px',
  },
};

export const AddFunnelMenu = ({
  disabled,
  allowedMenuActions = DEFAULT_ALLOWED_MENU_ACTIONS,
  menuPlacement,
  anchorOrigin,
  transformOrigin,
  onClickMenuItemCallback,
}: {
  disabled: boolean;
  allowedMenuActions?: AddFunnelMenuActions[];
  menuPlacement?: MenuPlacement;
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
  onClickMenuItemCallback?: (action: AddFunnelMenuActions) => void;
}) => {
  const { maybeOpenPanel } = usePanels();
  const handleCommentsAndAiFirst = useHandleCommentsAndAiFirst();
  const dispatch = useDispatch();
  const createFunnelsPermissions = useHasAllPermissions(['create:funnels', 'edit:funnels']);

  const baseActions = [
    {
      label: 'Add business process',
      value: AddFunnelMenuActions.IMPORT_FUNNEL,
      actionIcon: <CloudArrowDown size="medium" />,
      hidden: !allowedMenuActions.includes(AddFunnelMenuActions.IMPORT_FUNNEL),
      actionSx: TOOLBAR_ACTIONS_SX,
    },
    {
      label: 'External Processes',
      value: AddFunnelMenuActions.ADD_3TH_PARTY_FUNNEL,
      actionIcon: <Integrations size="medium" />,
      disabled: !createFunnelsPermissions,
      disabledTooltip: 'To add new funnels, please contact your admin.',
      hidden: !allowedMenuActions.includes(AddFunnelMenuActions.ADD_3TH_PARTY_FUNNEL),
      actionSx: TOOLBAR_ACTIONS_SX,
    },
  ];

  const actions = baseActions.filter((action) => !action.hidden);

  return (
    <ActionsMenu
      buttonTestId="add-funnel-menu-button"
      actions={actions}
      menuPlacement={menuPlacement}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      onClick={(action) => {
        onClickMenuItemCallback?.(action.value);
        switch (action.value) {
          case AddFunnelMenuActions.IMPORT_FUNNEL:
            handleCommentsAndAiFirst(() => {
              maybeOpenPanel({ panelType: importFunnelPanelType });
            });
            break;
          case AddFunnelMenuActions.ADD_3TH_PARTY_FUNNEL:
            dispatch(setAddOrImportDialogOpen('import'));
            break;
          default:
            break;
        }
      }}
      MenuButtonIcon={<Plus size="medium" />}
      iconSize="tiny"
      iconVariant="primary"
      disabled={disabled}
    />
  );
};
